// Third-party imports
import React, { useState } from 'react';
import {
  RadioButtonGroupInput,
  SaveButton,
  SelectInput,
  useRecordContext,
  FormDataConsumer,
  useInput,
} from 'react-admin';
import { Box, Typography, Alert, TextField, InputAdornment } from '@mui/material';
import { CopyIcon, Button } from '@troverecommerce/saas-components-ts/build';

// Local imports - types
import type { RepairDetail } from '@src/types';

// Local imports - components
import { DashboardCurrency } from '@src/utils/valueFormatters';
import { REJECTION_REASONS } from '@src/types/constants';

const calculateTotalPrice = (
  record: RepairDetail,
  issueStates: Record<string, 'APPROVED' | 'REJECTED'>,
  issuePrices: Record<string, number>,
  isFree: boolean
) => {
  if (isFree) return 0;

  let total = 0;
  record.repairItems?.forEach((item) => {
    item.repairItemIssues?.forEach((issue) => {
      // Only add price if issue is not rejected
      if (issueStates[issue.uuid] !== 'REJECTED') {
        const price = issuePrices[issue.uuid] !== undefined ?
          Number(issuePrices[issue.uuid]) :
          (issue.price || 0);
        total += price;
      }
    });
  });

  return total;
};

const EditStatusSection = () => {
  const record = useRecordContext<RepairDetail>();
  const shippingPriceField = useInput({ source: 'shippingPrice' });
  const [isEditingShippingPrice, setIsEditingShippingPrice] = useState(false);
  const [editedShippingPrice, setEditedShippingPrice] = useState('');

  if (!record) return null;

  const handleEditShippingPrice = (currentPrice: number) => {
    setEditedShippingPrice(String(currentPrice || 0));
    setIsEditingShippingPrice(true);
  };

  const handleSaveShippingPrice = () => {
    const newPrice = parseFloat(editedShippingPrice);
    if (!isNaN(newPrice) && newPrice >= 0) {
      shippingPriceField.field.onChange(newPrice);
      setIsEditingShippingPrice(false);
    }
  };

  const handleCancelShippingPriceEdit = () => {
    setIsEditingShippingPrice(false);
    setEditedShippingPrice('');
  };

  return (
    <Box sx={{ padding: 2, border: '5px solid red', borderRadius: 1 }}>
      <Alert severity="warning" sx={{ marginBottom: 3, display: 'flex', justifyContent: 'center' }}>
        The repair request requires a status update
      </Alert>

      <Typography variant="h6">
        Repair Status
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography>
          Repair ID:
          {' '}
          {record.uuidPrefix}
        </Typography>
        <CopyIcon name="Repair ID" value={record.uuidPrefix} />
      </Box>
      <Typography variant="body2" color="text.secondary">
        Select from the available options below to change the status of this repair order.
      </Typography>

      {/* Note: APPROVED_FREE is a UI-only state transformed to APPROVED (with $0 prices) before sending to backend */}
      <RadioButtonGroupInput
        source="repairStatus"
        choices={[
          {
            id: 'APPROVED',
            name: 'Accept Repair Request & Require Payment - Sends the customer a payment request. A shipping label is sent after their payment is complete.',
          },
          {
            id: 'APPROVED_FREE',
            name: 'Accept as Free Repair - Sends the customer a shipping label, with no payment required.',
          },
          {
            id: 'REJECTED',
            name: 'Reject Entire Repair Request - Notifies the customer that their repair request is canceled. A rejection reason must be selected.',
          },
        ]}
        sx={{ marginTop: 1 }}
      />

      <FormDataConsumer>
        {({ formData }) => {
          const isFree = formData.repairStatus === 'APPROVED_FREE';
          const totalRepairPrice = calculateTotalPrice(
            record,
            formData.issueStates || {},
            formData.issuePrices || {},
            isFree
          );

          return (
            <Box sx={{ marginLeft: 4 }}>
              {formData.repairStatus === 'APPROVED' && (
                <>
                  <Typography variant="body2" gutterBottom>
                    <strong>Repair Payment Required:</strong>
                    {' '}
                    {DashboardCurrency.displayValue(totalRepairPrice, record.currencyCode)}
                  </Typography>

                  <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Shipping Payment Required:</strong>
                    </Typography>
                    {isEditingShippingPrice ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          label="Shipping Payment Required"
                          type="number"
                          inputProps={{
                            min: '0',
                            step: '0.01',
                          }}
                          value={editedShippingPrice}
                          onChange={e => setEditedShippingPrice(e.target.value)}
                          size="small"
                          sx={{ width: 200 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {record.currencyCode}
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button label={'OK'} onClick={handleSaveShippingPrice} />
                        <Button label={'Cancel'} onClick={handleCancelShippingPriceEdit} />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {DashboardCurrency.displayValue((shippingPriceField.field.value || 0), record.currencyCode)}
                        </Typography>
                        <Button
                          label="Edit"
                          variant="text"
                          onClick={() => handleEditShippingPrice(shippingPriceField.field.value || 0)}
                          sx={{
                            textDecoration: 'underline',
                            minWidth: 'auto',
                            padding: 0,
                            textTransform: 'none',
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {formData.repairStatus === 'APPROVED_FREE' && (
                <Typography variant="body2" sx={{ marginBottom: 4 }}>
                  <strong>Promo code added by customer:</strong>
                  {' '}
                  {record.promoCode || 'N/A'}
                </Typography>
              )}

              {formData.repairStatus === 'REJECTED' && (
                <SelectInput
                  source="repairRejectionReason"
                  label="Rejection Reason"
                  choices={REJECTION_REASONS.map(reason => ({ id: reason, name: reason }))}
                  sx={{ maxWidth: 500 }}
                  validate={[required => 'Rejection reason is required']}
                />
              )}
            </Box>
          );
        }}
      </FormDataConsumer>

      <Box sx={{ marginBottom: 1, padding: 2, backgroundColor: 'grey.50', borderRadius: 1, width: 'fit-content' }}>
        <Typography color="text.secondary">
          Note: When you submit this form, the customer will be notified of your decision.
          Make sure all selections are correct before submitting.
        </Typography>
      </Box>
      <SaveButton label="Submit & Notify Customer" alwaysEnable></SaveButton>
    </Box>
  );
};

export default EditStatusSection;
